import {
  MeApi,
  MeDataExportsApi,
  TakedownNoticesApi,
  ProblemReportsApi,
  HeartbeatApi,
  DiscoveryApi,
  PreviewApi,
  CreatorIncidentFootageApi,
  CreatorIncidentImagesApi,
  FeaturedApi,
  Configuration,
  CreatorIncidentReportsApi,
  AdminIncidentReportsApi,
  AdminIncidentReportRelationsApi,
  AdminDashboardApi,
  AdminUserProfilesApi,
  AdminUserFilesApi,
  SessionApi,
  AdminIncidentImagesApi,
  AdminIncidentFootageApi,
  AdminBountiesApi,
  PublicBountiesApi,
  CreatorBountyResponsesApi,
  NotificationsApi,
  SearchApi,
  CrashesApi
} from '@roadfootage/api-client'
import type { ResponseContext } from '@roadfootage/api-client'
import { useAuth0 } from '@auth0/auth0-vue'
import { Notify } from 'quasar'

import { useSessionStore } from '@/stores/session'
import { useUserStore } from '@/stores/user'
import { isTermsUpdateRequired } from '@/utils/terms-gate'

// Auth0 error codes that indicate the session can no longer be refreshed silently.
// In these cases we degrade gracefully to the anonymous token rather than throwing.
const RECOVERABLE_AUTH_ERRORS = [
  'login_required',
  'invalid_grant',
  'consent_required',
  'missing_refresh_token'
]

export interface UseApiOptions {
  /** Set false for background calls (polling) so a failure is logged by the caller instead of shown as a toast. */
  notifyOnError?: boolean
}

export function useApi(options: UseApiOptions = {}) {
  const sessionStore = useSessionStore()
  const { isAuthenticated, getAccessTokenSilently } = useAuth0()

  async function resolveAccessToken(name?: string): Promise<string> {
    if (isAuthenticated.value) {
      try {
        const token = await getAccessTokenSilently()
        return `${name} ${token}`
      } catch (e: any) {
        const isRecoverable = RECOVERABLE_AUTH_ERRORS.some(
          (code) => e?.error === code || e?.message?.includes(code)
        )
        if (isRecoverable) {
          // Clear the stale Auth0 cache so _isAuthenticated drops to false.
          // This fires the watch(isLoggedIn) watcher in App.vue which redirects
          // to featured and allows the anonymous session (or Turnstile) to take over.
          // Without this, the app loops on broken silent-refresh indefinitely.
          await sessionStore.clearAuthSession()
          return `${name} ${sessionStore.anonymousAccessToken ?? ''}`
        }
        throw e
      }
    }
    return `${name} ${sessionStore.anonymousAccessToken ?? ''}`
  }

  // The API refuses writes until the user accepts the current terms. Tell the user store so the app can send them to the
  // "Updated terms" screen. Runs in both modes (quiet calls too) and never shows an error toast for that answer.
  async function termsGate(ctx: ResponseContext): Promise<Response | void> {
    if (await isTermsUpdateRequired(ctx?.response)) useUserStore().markTermsUpdateRequired()
  }

  async function notifyOnError(ctx: ResponseContext): Promise<Response | void> {
    if (ctx?.response?.status === 404) return
    if (await isTermsUpdateRequired(ctx?.response)) return
    if (ctx?.response?.status === 401) {
      Notify.create({
        type: 'warning',
        icon: 'mdi-account-clock-outline',
        message: 'Your session has expired. Please sign in again.'
      })
      return
    }
    if (ctx?.response?.status && ctx.response.status >= 400) {
      Notify.create({
        color: 'red-5',
        textColor: 'white',
        icon: 'mdi-alert-box',
        message: `Something went wrong processing your request. Reason: ${ctx.response.statusText}`
      })
    }
  }

  const config = new Configuration({
    accessToken: resolveAccessToken,
    middleware:
      options.notifyOnError === false
        ? [{ post: termsGate }]
        : [{ post: termsGate }, { post: notifyOnError }],
    basePath: import.meta.env.VITE_API_BASE_URL
  })

  return {
    // Public APIs
    sessionApi: new SessionApi(config),

    // Anonymous APIs
    discoveryApi: new DiscoveryApi(config),
    heartbeatApi: new HeartbeatApi(config),
    featuredApi: new FeaturedApi(config),
    searchApi: new SearchApi(config),
    crashesApi: new CrashesApi(config),
    takedownNoticesApi: new TakedownNoticesApi(config),
    problemReportsApi: new ProblemReportsApi(config),
    notificationsApi: new NotificationsApi(config),
    previewApi: new PreviewApi(config),

    // User/creator APIs
    meApi: new MeApi(config),
    meDataExportsApi: new MeDataExportsApi(config),
    creatorIncidentReportsApi: new CreatorIncidentReportsApi(config),
    creatorIncidentFootageApi: new CreatorIncidentFootageApi(config),
    creatorIncidentImagesApi: new CreatorIncidentImagesApi(config),
    creatorBountyResponsesApi: new CreatorBountyResponsesApi(config),

    // Staff APIs
    adminIncidentReportsApi: new AdminIncidentReportsApi(config),
    adminIncidentReportRelationsApi: new AdminIncidentReportRelationsApi(config),
    adminUserProfilesApi: new AdminUserProfilesApi(config),
    adminUserFilesApi: new AdminUserFilesApi(config),
    adminIncidentFootageApi: new AdminIncidentFootageApi(config),
    adminIncidentImagesApi: new AdminIncidentImagesApi(config),
    adminBountiesApi: new AdminBountiesApi(config),
    adminDashboardApi: new AdminDashboardApi(config),

    publicBountiesApi: new PublicBountiesApi(config)
  }
}
